import { useCallback, useEffect, useMemo } from 'react';

import type { Campaign } from 'src/common/interfaces/campaigns';

import { readStoredCampaigns, storeCampaigns } from '../helpers/campaign-storage';

export const useCampaignStorage = (current: Campaign | null) => {
  // Clean up obsolete campaigns from storage
  useEffect(() => {
    const storedCampaigns = readStoredCampaigns();
    const now = Date.now();
    const { seenCampaigns, activeCampaign } = storedCampaigns;
    const cleanedUp = seenCampaigns.filter(
      (storedCampaign) => !storedCampaign.endTime || storedCampaign.endTime >= now,
    );
    const activeCampaignToStore = activeCampaign && activeCampaign?.sessionExpiration > now ? activeCampaign : null;

    if (activeCampaignToStore === activeCampaign && seenCampaigns.length === cleanedUp.length) {
      return;
    }

    storeCampaigns({
      activeCampaign: activeCampaignToStore,
      seenCampaigns: cleanedUp,
    });
  }, []);

  const markAsSeen = useCallback(() => {
    if (!current) return;

    const storedCampaigns = readStoredCampaigns();

    const index = storedCampaigns.seenCampaigns.findIndex((c) => c.name === current.name);
    const campaign = index !== -1 ? storedCampaigns.seenCampaigns.splice(index, 1)?.[0] : undefined;

    storeCampaigns({
      ...storedCampaigns,
      seenCampaigns: [
        ...storedCampaigns.seenCampaigns,
        {
          ...campaign,
          name: current.name,
          endTime: current.endTime,
          lastSeen: Date.now(),
        },
      ],
    });
  }, [current]);

  const { isSeen, lastSeen } = useMemo(() => {
    if (!current) return { isSeen: false, lastSeen: undefined };

    const campaign = readStoredCampaigns().seenCampaigns.find((an) => an.name === current.name);

    return { isSeen: !!campaign, lastSeen: campaign?.lastSeen };
  }, [current]);

  const reset = () => {
    if (!current) return;

    const storedCampaigns = readStoredCampaigns();
    const removed = storedCampaigns.seenCampaigns.filter((campaign) => campaign.name !== current.name);

    storeCampaigns({
      ...storedCampaigns,
      seenCampaigns: removed,
    });
  };

  return { isSeen, markAsSeen, lastSeen, reset };
};
